import { CachedItemType } from "@/types/caching/cachedItemType"
import Vue from "vue"

/**
 * A generic class for managing cached data and handling data fetching from an external source.
 * Provides methods for retrieving, updating and managing the lifecycle of cached items,
 * including cache expiration, refresh and error handling.
 *
 * @template T - The type of the objects to be cached.
 */
export default class DataCache<T extends CachedObject> {
	private cache: T[] = []
	private cacheMap: { [key: string]: T } = {}

	public readonly cacheKey: CachedItemType
	private readonly fetchMethod: () => Promise<T[]>
	private readonly buildMap: boolean = false
	private readonly expireAfterMins: number = 180
	private fetchPromise: Promise<T[]> | null = null
	private lastFetchedAt: Date | null = null
	private pendingRefresh: boolean = true
	private _timesFetched: number = 0

	/**
	 * Initialises a new DataCache instance for managing and caching data.
	 *
	 * @param cacheKey - A unique key of type `CachedItemType` for this cache instance.
	 * @param fetchMethod - An async function that retrieves the data to be cached.
	 * @param expireAfterMins - (Optional) The number of minutes after which the cache is considered expired. Defaults to 180 minutes.
	 * @param buildMap - (Optional) If true, builds an internal map for fast lookup by ID. Defaults to false.
	 */
	constructor (cacheKey: CachedItemType, fetchMethod: () => Promise<T[]>, expireAfterMins: number = 180, buildMap: boolean = false) {
		this.cacheKey = cacheKey
		this.fetchMethod = fetchMethod
		this.expireAfterMins = expireAfterMins
		this.buildMap = buildMap
	}

	/**
	 * Gets the number of times the cache has been fetched.
	 *
	 * @returns The number of times the cache has been fetched.
	 */
	public get timesFetched (): number {
		return this._timesFetched
	}

	/**
	 * Determines if the data is currently being fetched.
	 *
	 * @returns A boolean indicating if the data is being fetched.
	 */
	public get isFetching (): boolean {
		return this.fetchPromise !== null
	}

	/**
	 * Determines if any data has been fetched for this cache.
	 *
	 * @returns A boolean indicating if data has been fetched.
	 */
	public get hasFetchedData (): boolean {
		return this.timesFetched > 0
	}

	/**
	 * Retrieves an item from the cache by its ID.
	 * Logs an error and returns undefined if the cache has never been fetched.
	 *
	 * @param id - The ID of the item to retrieve.
	 * @returns The cached item with the specified ID, or undefined if not found or never fetched.
	 */
	public getById (id: string): T | undefined {
		if (this.logErrorIfNeverFetched("ID: " + id).hasNeverFetched) {
			return undefined
		}

		return this.buildMap
			? this.cacheMap[id]
			: this.cache.find(item => item.id === id)
	}

	/**
	 * Returns all items currently in the cache.
	 * Logs an error if the cache has never been fetched.
	 *
	 * @returns An array of cached items.
	 */
	public get items (): T[] {
		this.logErrorIfNeverFetched()
		return this.cache
	}

	/**
	 * Returns the cached items as a map keyed by item ID.
	 * Logs an error if the cache has never been fetched.
	 * Throws an error if the cache was not built as a map.
	 *
	 * @returns An object mapping item IDs to cached items.
	 * @throws Error if buildMap is false.
	 */
	public get itemMap (): { [key: string]: T } {
		this.logErrorIfNeverFetched()
		if (!this.buildMap) throw new Error("Cannot get map when buildMap is false. Cache: " + this.cacheKey)
		return this.cacheMap
	}

	/**
	 * Fetches data from the source if a refresh is pending or the cache is expired.
	 * If a fetch is already in progress, waits for it to complete.
	 * Otherwise, returns the current cached data.
	 *
	 * @returns A promise resolving to the cached data array.
	 */
	public async fetchIfNeeded (): Promise <T[]> {
		if (!this.fetchPromise && (this.pendingRefresh || this.isCacheExpired())) {
			console.log("Fetching data for cache: " + this.cacheKey)
			await this.fetch()
		} else if (this.fetchPromise) {
			await this.fetchPromise
		}

		return this.cache
	}

	/**
	 * Forces a fetch of data from the source, regardless of cache state or expiration.
	 *
	 * @returns A promise resolving to the updated cached data array.
	 */
	public async fetchAll (): Promise <T[]> {
		await this.fetch()
		return this.cache
	}

	/**
	 * Sets the pending refresh flag.
	 *
	 * @param value - Boolean indicating whether a refresh is pending.
	 */
	public setPendingRefresh (value: boolean): void{
		this.pendingRefresh = value
	}

	/**
	 * Handles fetching data from the source and updating the cache.
	 * Handles updating fetch state, cache contents and error handling.
	 * Updates the cache map if buildMap is enabled.
	 * Sets the pendingRefresh flag to false on success, or true on failure.
	 *
	 * @private
	 * @throws Error if the fetch operation fails.
	 */
	private async fetch () {
		let promiseResolve: any = null
		try {
			this.lastFetchedAt = new Date()
			Vue.set(this, "fetchPromise", new Promise((resolve, reject) => promiseResolve = resolve))
			const dataPromise = this.fetchMethod()
			this.cache = await dataPromise
			Vue.set(this, "_timesFetched", this.timesFetched + 1)

			if (this.buildMap) {
				this.cacheMap = this.cache.reduce((acc, item) => {
					acc[item.id as string] = item
					return acc
				}, {} as { [key: string]: T })
			}

			this.pendingRefresh = false
		} catch (e) {
			this.pendingRefresh = true
			throw new Error("Failed to fetch data for cache: " + this.cacheKey + ". Error: " + e!.toString())
		} finally {
			if (promiseResolve !== null) promiseResolve(this.cache)
			Vue.set(this, "fetchPromise", null)
		}
	}

	/**
	 * Logs an error if data is accessed before it has ever been fetched.
	 *
	 * @param additionalInfo - Optional string for extra error context.
	 * @returns An object indicating if data has never been fetched.
	 * @private
	 */
	private logErrorIfNeverFetched (additionalInfo: string = ""): { hasNeverFetched: boolean } {
		if (!this.hasFetchedData) {
			this.logGetBeforeFetchError(additionalInfo)
		}

		return { hasNeverFetched: !this.hasFetchedData }
	}

	/**
     * Logs a standardised error message for accessing cache before fetch.
     *
     * @param additionalInfo - Additional context for the error message.
     * @private
     */
	private logGetBeforeFetchError (additionalInfo: string): void {
		console.error(`Attempted to get data from cache before it was fetched. Cache: ${this.cacheKey}. ${additionalInfo}`)
	}

	/**
	 * Determines if the cache has expired based on the configured expiration time.
	 *
	 * @returns True if the cache is expired, false otherwise.
	 * @private
	 */
	private isCacheExpired (): boolean {
		if (!this.lastFetchedAt) return true
		const now = new Date()
		const diff = now.getTime() - this.lastFetchedAt.getTime()
		const diffMins = Math.floor(diff / 1000 / 60)
		return diffMins >= this.expireAfterMins
	}
}

export interface CachedObject {
	id?: string | null
}
